import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query';

import {
  AddDirectoryParams,
  AddDirectoryResponse,
  FileList,
  FileListArgs,
  FileViewerCompanies,
  FileViewerCompaniesResponse,
  UploadFileParams,
  UploadFileResponse,
  transformFileListReponse,
} from 'helper/products/fileViewer';
import { isCompanyId, toFileViewerCompanies } from 'helper/products/fileViewerCompanies';
import getProductKey from 'helper/requests/getProductKey';
import baseApi from './base';

const fileViewerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFileList: build.query<FileList, FileListArgs>({
      // `empresa` (admin) só entra na URL como inteiro positivo; `{}` é exatamente a URL de hoje.
      // A product_key fica em primeiro: o baseQuery a reescreve por regex na recuperação do 403.
      query: ({ empresa, refresh }: FileListArgs = {}) => {
        const productKey = getProductKey('file-viewer');
        let url = `produtos/file-viewer/get-file-list/?product_key=${productKey}`;
        if (isCompanyId(empresa)) url += `&empresa=${empresa}`;
        // Admin: o backend relê o disco (uma vez por minuto por usuário) em vez de servir o cache.
        if (refresh) url += '&refresh=1';
        return url;
      },
      // `refresh` não entra na chave: a lista relida cai na MESMA entrada que a tela já mostra.
      serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) => (
        defaultSerializeQueryArgs({
          queryArgs: { empresa: queryArgs?.empresa },
          endpointDefinition,
          endpointName,
        })
      ),
      transformResponse: transformFileListReponse,
      // A árvore fica no cache por 1 h depois de sair da tela: voltar mostra a lista na hora. Os
      // tokens de download/upload dela valem 4 h e, vencidos, os hooks renovam a lista e repetem.
      keepUnusedDataFor: 3600,
    }),
    listFileViewerCompanies: build.query<FileViewerCompanies, void>({
      query: () => {
        const productKey = getProductKey('file-viewer');
        return `produtos/file-viewer/empresas/?product_key=${productKey}`;
      },
      transformResponse: (response: FileViewerCompaniesResponse) => (
        toFileViewerCompanies(response.data)
      ),
      // Empresas mudam pouco: 30 min em cache depois de sair da tela. Não-admin recebe a lista
      // vazia (code 200), não 403.
      keepUnusedDataFor: 1800,
    }),
    addDirectory: build.mutation<AddDirectoryResponse, AddDirectoryParams>({
      query: ({ formattedPath }) => {
        const productKey = getProductKey('file-viewer');
        return {
          url: `produtos/file-viewer/new-directory?product_key=${productKey}`,
          method: 'PUT',
          body: { final_dir: formattedPath },
        };
      },
    }),
    uploadFile: build.mutation<UploadFileResponse, UploadFileParams>({
      query: (params) => {
        const productKey = getProductKey('file-viewer');
        return {
          url: `produtos/file-viewer/upload-file?product_key=${productKey}`,
          method: 'PUT',
          body: {
            file_base64: params.fileBase64,
            dir_info: params.formattedPath,
            dir_token: params.dirName,
            file_name: params.filename,
          },
        };
      },
    }),
  }),
});

export const {
  useGetFileListQuery,
  useLazyGetFileListQuery,
  useListFileViewerCompaniesQuery,
  useAddDirectoryMutation,
  useUploadFileMutation,
} = fileViewerApi;
